import reactUpdate from 'immutability-helper';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {EventConstants} from 'constants/EventConstants';
import AppDispatcher, {FluxAction} from 'lib/Dispatcher';
import Store from 'lib/Store';

import {AssemblyWithOptionalMeta, BULK_ASSEMBLY_STATE, LocalAssembly} from './BulkAssemblyConstants';
import * as BulkAssemblyUtil from './BulkAssemblyUtil';

class BulkAssemblyStore extends Store {
  assembly!: LocalAssembly;

  override resetState(): void {
    this.assembly = BulkAssemblyUtil.createAssembly();
  }

  getAssembly(): LocalAssembly {
    return this.assembly;
  }

  /**
   * Return whether or not any field in the assembly has changed (excluding any meta data such as state,
   * active slot, and validation results).
   *
   * Used to decide if async validation results are still valid (this is notably defensive in that there are
   * fields that do not require re-validation like `targetFolderId`).
   */
  _hasChangedFrom(oldAssembly: LocalAssembly): boolean {
    return !isEqual(
      reactUpdate(oldAssembly as AssemblyWithOptionalMeta, {meta: {$set: undefined}}),
      reactUpdate(this.assembly as AssemblyWithOptionalMeta, {meta: {$set: undefined}})
    );
  }

  override payloadHandler(action: FluxAction): boolean {
    switch (action.actionType) {
      case EventConstants.BULK_ASSEMBLY_SET_TOPOLOGY: {
        this.assembly = BulkAssemblyUtil.Update.setTopology(this.assembly, action.topology);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_ADD_SLOT: {
        this.assembly = BulkAssemblyUtil.Update.addSlot(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_SLOT_DATA_SOURCE: {
        const {slotIdx, dataSourceId} = action;
        this.assembly = BulkAssemblyUtil.Update.setSlotDataSource(this.assembly, slotIdx, dataSourceId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_UPDATE_SLOT: {
        const {slotIdx, updates} = action;
        this.assembly = BulkAssemblyUtil.Update.updateSlot(this.assembly, slotIdx, updates);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_REMOVE_SLOT: {
        const {slotIdx} = action;
        this.assembly = BulkAssemblyUtil.Update.removeSlot(this.assembly, slotIdx);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_NEXT_SLOT: {
        this.assembly = BulkAssemblyUtil.Update.nextSlot(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_PREVIOUS_SLOT: {
        this.assembly = BulkAssemblyUtil.Update.previousSlot(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_UPDATE_DATA_SOURCE: {
        const {dataSourceId, updates} = action;
        this.assembly = BulkAssemblyUtil.Update.updateDataSource(this.assembly, dataSourceId, updates);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_ACTIVATE_STEP: {
        const {slotIdx} = action;
        this.assembly = BulkAssemblyUtil.Update.activateStep(this.assembly, slotIdx);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RESET_SLOT_DATA_SOURCE: {
        const {slotIdx, dataSourceType} = action;
        this.assembly = BulkAssemblyUtil.Update.resetSlotDataSource(this.assembly, slotIdx, dataSourceType);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RUN: {
        this.assembly = BulkAssemblyUtil.Update.updateForRun(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RUN_COMPLETED: {
        this.assembly = BulkAssemblyUtil.Update.updateForCompletedRun(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RUN_FAILED: {
        this.assembly = BulkAssemblyUtil.Update.updateForFailedRun(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_TAG_SCHEMA_ID: {
        const {tagSchemaId} = action;
        this.assembly = BulkAssemblyUtil.Update.setTagSchemaId(this.assembly, tagSchemaId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RESET_SCHEMA_FIELDS: {
        this.assembly = BulkAssemblyUtil.Update.resetSchemaFields(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_USE_SEQUENCE_WORKLIST: {
        const {useWorklist} = action;
        this.assembly = BulkAssemblyUtil.Update.setUseSequenceWorklist(this.assembly, useWorklist);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_USE_PRIMER_WORKLIST: {
        const {useWorklist} = action;
        this.assembly = BulkAssemblyUtil.Update.setUsePrimerWorklist(this.assembly, useWorklist);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_USE_FRAGMENT_WORKLIST: {
        const {useWorklist} = action;
        this.assembly = BulkAssemblyUtil.Update.setUseFragmentWorklist(this.assembly, useWorklist);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_SEQUENCE_WORKLIST: {
        const {newOrExisting} = action;
        this.assembly = BulkAssemblyUtil.Update.setSequenceWorklistFormType(this.assembly, newOrExisting);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_PRIMER_WORKLIST: {
        const {newOrExisting} = action;
        this.assembly = BulkAssemblyUtil.Update.setPrimerWorklistFormType(this.assembly, newOrExisting);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_OR_EXISTING_FRAGMENT_WORKLIST: {
        const {newOrExisting} = action;
        this.assembly = BulkAssemblyUtil.Update.setFragmentWorklistFormType(this.assembly, newOrExisting);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_EXISTING_SEQUENCE_WORKLIST_ID: {
        const {existingWorklistId} = action;
        this.assembly = BulkAssemblyUtil.Update.setSequenceWorklistId(this.assembly, existingWorklistId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_EXISTING_PRIMER_WORKLIST_ID: {
        const {existingWorklistId} = action;
        this.assembly = BulkAssemblyUtil.Update.setPrimerWorklistId(this.assembly, existingWorklistId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_EXISTING_FRAGMENT_WORKLIST_ID: {
        const {existingWorklistId} = action;
        this.assembly = BulkAssemblyUtil.Update.setFragmentWorklistId(this.assembly, existingWorklistId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_SEQUENCE_WORKLIST_INFO: {
        const {newWorklistInfo} = action;
        this.assembly = BulkAssemblyUtil.Update.setNewSequenceWorklistInfo(this.assembly, newWorklistInfo);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_PRIMER_WORKLIST_INFO: {
        const {newWorklistInfo} = action;
        this.assembly = BulkAssemblyUtil.Update.setNewPrimerWorklistInfo(this.assembly, newWorklistInfo);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SET_NEW_FRAGMENT_WORKLIST_INFO: {
        const {newWorklistInfo} = action;
        this.assembly = BulkAssemblyUtil.Update.setNewFragmentWorklistInfo(this.assembly, newWorklistInfo);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_SEQUENCE_FOLDER_ID: {
        const {folderId} = action;
        this.assembly = BulkAssemblyUtil.Update.setSequenceFolderId(this.assembly, folderId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_PRIMER_FOLDER_ID: {
        const {folderId} = action;
        this.assembly = BulkAssemblyUtil.Update.setPrimerFolderId(this.assembly, folderId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_FRAGMENT_FOLDER_ID: {
        const {folderId} = action;
        this.assembly = BulkAssemblyUtil.Update.setFragmentFolderId(this.assembly, folderId);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RESET: {
        assert(
          this.assembly.meta.state !== BULK_ASSEMBLY_STATE.ASSEMBLING,
          'User unexpectedly tried to reset assembly with assembly in progress'
        );
        this.assembly = BulkAssemblyUtil.createAssembly();
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_RESET_SOURCES_AND_OUTPUT: {
        this.assembly = BulkAssemblyUtil.cloneAssembly(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_VALIDATE: {
        this.assembly = BulkAssemblyUtil.Update.updateForValidation(this.assembly);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_VALIDATE_COMPLETED: {
        if (this._hasChangedFrom(action.assembly)) {
          break;
        }
        this.assembly = BulkAssemblyUtil.Update.updateForCompletedValidation(this.assembly, action.summary);
        this.emitChange();
        break;
      }
      case EventConstants.BULK_ASSEMBLY_VALIDATE_FAILED: {
        if (this._hasChangedFrom(action.assembly)) {
          break;
        }
        this.assembly = BulkAssemblyUtil.Update.updateForFailedValidation(this.assembly);
        this.emitChange();
        break;
      }
      default:
        break;
    }
    return true;
  }
}

const bulkAssemblyStore = new BulkAssemblyStore(AppDispatcher);
export default bulkAssemblyStore;
